import React, { useCallback, useEffect, useState } from 'react';
import { bearerHeaders } from '../api.ts';
import { getTheme, useThemeMode } from '../theme';
import { apiFetch } from '../appFetch';
import { agoLabel, freshnessLabel, pajFreshness, PAJ_DOG_COLORS, type PajFreshness } from '../karta/pajDogs';

interface PajAccount {
  connected: boolean;
  email?: string;
  status?: 'ok' | 'error' | 'reauth';
  lastError?: string | null;
  pollIntervalSec?: number;
  lastPollAt?: number | null;
}

interface PajDeviceRow {
  deviceId: number;
  name: string | null;
  imeiTail: string | null;
  reportIntervalSec: number | null;
  storageDays: number | null;
  dogId: string | null;
  dogName: string | null;
  selected: boolean;
  lastPosition: {
    recordedAt: string; batteryPercent: number | null; speedKmh: number | null;
    staleAfterSec: number; lostAfterSec: number; freshness: PajFreshness;
  } | null;
}

/**
 * PAJ GPS (PET Finder 4G) i Profil → Integrationer: anslut kontot, välj hundens tracker.
 * Lösenordet skickas till vårt API en gång och sparas aldrig; API:t sparar bara krypterad token.
 */
export function PajIntegrationCard({ token, dogs }: { token: string; dogs: { id: string; name: string }[] }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  // Content-Type bara när det finns en kropp – Fastify avvisar tom JSON-kropp (DELETE).
  const H = { ...bearerHeaders(token), 'X-Api-Key': 'dev-secret' };
  const HJ = { ...H, 'Content-Type': 'application/json' };
  const [account, setAccount] = useState<PajAccount | null>(null);
  const [devices, setDevices] = useState<PajDeviceRow[]>([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/profile/integrations/paj', { headers: H });
      const a = r.ok ? await r.json() as PajAccount : { connected: false };
      setAccount(a);
      if (a.connected) {
        const d = await apiFetch('/api/profile/integrations/paj/devices', { headers: H });
        if (d.ok) setDevices(await d.json());
      } else setDevices([]);
    } catch { setAccount({ connected: false }); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);
  // Status och senaste positioner uppdateras medan kortet är öppet (från vårt API, inte PAJ).
  useEffect(() => {
    if (!account?.connected) return;
    const id = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(id);
  }, [account?.connected, load]);

  const connect = async () => {
    setBusy('connect'); setMsg(null);
    try {
      const r = await apiFetch('/api/profile/integrations/paj', { method: 'POST', headers: HJ, body: JSON.stringify({ email, password }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg(d.error ?? 'Kunde inte ansluta till PAJ'); return; }
      setPassword(''); setEmail('');
      setAccount(d.account); setDevices(d.devices ?? []);
      setMsg(d.devices?.length ? 'Anslutet. Välj vilken tracker som sitter på vilken hund.' : 'Anslutet, men kontot har inga enheter ännu.');
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); setPassword(''); }
  };

  const refreshList = async () => {
    setBusy('refresh'); setMsg(null);
    try {
      const r = await apiFetch('/api/profile/integrations/paj/devices?refresh=1', { headers: H });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg(d.error ?? 'Kunde inte hämta enheterna'); await load(); return; }
      setDevices(d);
    } finally { setBusy(null); }
  };

  const link = async (dev: PajDeviceRow, dogId: string, selected: boolean) => {
    setBusy(`dev:${dev.deviceId}`); setMsg(null);
    try {
      const dog = dogs.find(x => x.id === dogId);
      const r = await apiFetch(`/api/profile/integrations/paj/devices/${dev.deviceId}`, {
        method: 'PUT', headers: HJ, body: JSON.stringify({ dogId: dogId || null, dogName: dog?.name || null, selected }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg(d.error ?? 'Kunde inte spara'); return; }
      setDevices(d);
      // Positionen hämtas direkt av servern – visa den så snart den finns.
      if (selected) { setTimeout(() => { void load(); }, 3000); setTimeout(() => { void load(); }, 20_000); }
    } finally { setBusy(null); }
  };

  const disconnect = async () => {
    if (!confirm('Koppla bort PAJ GPS? Token, enhetskopplingar och positioner raderas från Qvenari.')) return;
    const r = await apiFetch('/api/profile/integrations/paj', { method: 'DELETE', headers: H }).catch(() => null);
    if (!r?.ok) { setMsg('Kunde inte koppla bort PAJ. Försök igen.'); await load(); return; }
    setAccount({ connected: false }); setDevices([]); setMsg('PAJ GPS är bortkopplat.');
  };

  const lbl: React.CSSProperties = { color: T.textSub, fontSize: 13, marginBottom: 4, display: 'block' };
  const inp: React.CSSProperties = { background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 4, padding: '8px 12px', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
  const btn = (bg: string, fg = '#000'): React.CSSProperties => ({ background: bg, border: 'none', color: fg, padding: '9px 18px', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' });
  const connected = !!account?.connected;
  const reauth = account?.status === 'reauth';

  return (
    <div data-testid="paj-card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 22, filter: connected ? 'none' : 'grayscale(0.7)', opacity: connected ? 1 : 0.6 }}>🐕</span>
        <div>
          <div style={{ color: T.text, fontWeight: 700, fontSize: 16 }}>PAJ GPS</div>
          <div style={{ color: !connected ? T.textSub : reauth || account?.status === 'error' ? T.danger : '#61D836', fontSize: 12 }}>
            {!connected ? '○ Inte ansluten'
              : reauth ? '● Inloggningen har gått ut – anslut igen'
              : account?.status === 'error' ? `● Ansluten – ${account.email} · senaste hämtningen misslyckades`
              : `● Ansluten – ${account?.email}`}
          </div>
        </div>
      </div>

      {connected && !reauth ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ color: T.textSub, fontSize: 12 }}>
            Qvenari hämtar positionen var {Math.round((account?.pollIntervalSec ?? 60))}:e sekund för valda trackers{account?.lastPollAt ? ` · senast ${agoLabel(new Date(account.lastPollAt).toISOString())}` : ''}.
            {account?.status === 'error' && account.lastError ? <span style={{ color: T.danger }}> {account.lastError}</span> : null}
          </div>
          <div style={{ background: 'rgba(0,0,0,0.15)', borderRadius: 6, padding: 12 }}>
            <div style={{ color: T.text, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Dina trackers ({devices.length})</div>
            {devices.length === 0 && <div style={{ color: T.textMuted, fontSize: 12, fontStyle: 'italic' }}>Inga enheter på PAJ-kontot. Lägg till trackern i PAJ-appen och tryck Uppdatera listan.</div>}
            {devices.map(dev => {
              const pos = dev.lastPosition;
              const f = pos ? pajFreshness(pos) : null;
              return (
                <div key={dev.deviceId} data-testid={`paj-device-${dev.deviceId}`} style={{ borderBottom: `1px solid ${T.border}`, padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{dev.name || `Tracker ${dev.deviceId}`}</div>
                      <div style={{ color: T.textMuted, fontSize: 11 }}>
                        {dev.imeiTail ? `IMEI …${dev.imeiTail}` : `Id ${dev.deviceId}`}
                        {dev.reportIntervalSec ? ` · rapporterar var ${dev.reportIntervalSec}:e s` : ''}
                        {dev.storageDays != null ? (dev.storageDays > 0 ? ` · historik ${dev.storageDays} dygn` : ' · ingen spårhistorik') : ''}
                      </div>
                    </div>
                    {pos && f && (
                      <div style={{ fontSize: 12, color: f === 'live' ? '#61D836' : PAJ_DOG_COLORS[f], textAlign: 'right' }}>
                        ● {f === 'live' ? `Position ${agoLabel(pos.recordedAt)}` : freshnessLabel(pos)}
                        <div style={{ color: T.textSub }}>
                          {pos.batteryPercent != null ? `Batteri ${Math.round(pos.batteryPercent)} %` : ''}
                          {pos.speedKmh != null ? ` · ${Math.round(pos.speedKmh)} km/h` : ''}
                        </div>
                      </div>
                    )}
                    {!pos && dev.selected && <div style={{ fontSize: 12, color: T.textMuted }}>Väntar på första positionen …</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select aria-label={`Hund för ${dev.name || dev.deviceId}`} value={dev.dogId ?? ''} disabled={busy === `dev:${dev.deviceId}`}
                      onChange={e => { void link(dev, e.target.value, e.target.value ? true : dev.selected); }}
                      style={{ ...inp, width: 'auto', flex: 1, minWidth: 160, padding: '6px 10px', fontSize: 13 }}>
                      <option value="">— Ingen hund —</option>
                      {dogs.filter(d => d.name.trim()).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <label style={{ color: T.textSub, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={dev.selected} disabled={busy === `dev:${dev.deviceId}`}
                        onChange={e => { void link(dev, dev.dogId ?? '', e.target.checked); }} />
                      Visa på jaktkartan
                    </label>
                  </div>
                </div>
              );
            })}
            {dogs.filter(d => d.name.trim()).length === 0 && devices.length > 0 && (
              <div style={{ color: T.textMuted, fontSize: 12, marginTop: 8 }}>Lägg till din hund under Hundar ovan och spara profilen för att koppla trackern till den.</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={refreshList} disabled={busy === 'refresh'} style={{ ...btn(T.amber), opacity: busy === 'refresh' ? 0.5 : 1 }}>{busy === 'refresh' ? 'Hämtar…' : 'Uppdatera listan'}</button>
            <button onClick={disconnect} style={btn('#c0392b', '#fff')}>Koppla bort PAJ</button>
          </div>
        </div>
      ) : (
        <form onSubmit={e => { e.preventDefault(); void connect(); }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ color: T.textSub, fontSize: 12, lineHeight: 1.6 }}>
            Logga in med samma e-post och lösenord som i PAJ-appen (PAJ Finder Portal). Ditt lösenord sparas inte – Qvenari sparar bara en krypterad inloggningsnyckel från PAJ.
            {reauth ? <><br /><strong style={{ color: T.text }}>PAJ godtar inte längre den sparade inloggningen, t.ex. efter ett lösenordsbyte. Logga in igen.</strong></> : null}
          </div>
          <div><span style={lbl}>E-post för PAJ *</span><input style={inp} type="email" autoComplete="off" value={email} onChange={e => setEmail(e.target.value)} placeholder="din@epost.se" /></div>
          <div><span style={lbl}>Lösenord för PAJ *</span><input style={inp} type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Lösenord" /></div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="submit" disabled={!email || !password || busy === 'connect'} style={{ ...btn(T.amber), opacity: (!email || !password || busy === 'connect') ? 0.5 : 1 }}>{busy === 'connect' ? 'Ansluter…' : reauth ? 'Anslut igen' : 'Anslut PAJ GPS'}</button>
            {reauth && <button type="button" onClick={disconnect} style={btn('#c0392b', '#fff')}>Koppla bort</button>}
          </div>
        </form>
      )}
      {msg && <div role="status" style={{ marginTop: 10, color: T.textSub, fontSize: 13 }}>{msg}</div>}
    </div>
  );
}
